(() => {
  'use strict';

  const root = document.querySelector('[data-wge-pc-product]');
  if (!root) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  root.classList.add('wge-pc--js');

  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));

  const initRemote = (scope) => {
    const controls = Array.from(scope.querySelectorAll('[data-wge-remote-control]'));
    const states = Array.from(scope.querySelectorAll('[data-wge-remote-state]'));
    if (!controls.length || !states.length) return;

    const activate = (key) => {
      const target = states.find((state) => state.dataset.wgeRemoteState === key);
      if (!target) return;

      controls.forEach((control) => {
        control.setAttribute('aria-pressed', String(control.dataset.wgeRemoteControl === key));
      });
      states.forEach((state) => {
        state.hidden = state !== target;
      });
    };

    const initialControl = controls.find((control) => control.getAttribute('aria-pressed') === 'true') || controls[0];
    if (initialControl) activate(initialControl.dataset.wgeRemoteControl);

    controls.forEach((control) => {
      control.addEventListener('click', () => activate(control.dataset.wgeRemoteControl));
    });
  };

  const initFaq = (scope) => {
    scope.querySelectorAll('[data-wge-pc-faq]').forEach((faq) => {
      if (faq.dataset.wgeFaqBound === 'true') return;
      faq.dataset.wgeFaqBound = 'true';

      const disclosures = Array.from(faq.querySelectorAll('details'));
      disclosures.forEach((disclosure) => {
        disclosure.addEventListener('toggle', () => {
          if (!disclosure.open) return;

          disclosures.forEach((other) => {
            if (other !== disclosure && other.open) other.open = false;
          });

          const answer = disclosure.querySelector('[data-wge-faq-answer]');
          if (!answer || reducedMotion.matches || typeof answer.animate !== 'function') return;
          answer.animate(
            [
              { opacity: 0, transform: 'translateY(-0.35rem)' },
              { opacity: 1, transform: 'translateY(0)' }
            ],
            { duration: 170, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
          );
        });
      });
    });
  };

  const cameraTransform = (camera, offset) => {
    const local = clamp(offset, -1, 1);
    let x = 0;
    let y = 0;
    let scale = 1;

    switch (camera) {
      case 'dolly-in':
        scale = 1 + local * 0.045;
        x = local * -1.5;
        break;
      case 'lateral-left':
        x = local * -5.5;
        scale = 1.025 + Math.abs(local) * 0.015;
        break;
      case 'orbit-right':
        x = local * 5;
        y = local * -0.75;
        scale = 1.035 + Math.abs(local) * 0.012;
        break;
      case 'macro-dolly':
        x = local * -2.25;
        scale = 1.08 + local * 0.045;
        break;
      case 'light-awakening':
        scale = 1.02 + local * 0.025;
        break;
      case 'lateral-right':
        x = local * 4.5;
        scale = 1.025;
        break;
      case 'resolve':
        scale = 1.025 - local * 0.035;
        x = local * 1.5;
        break;
      default:
        scale = 1.015;
    }

    return `translate3d(${x.toFixed(3)}%, ${y.toFixed(3)}%, 0) scale(${scale.toFixed(4)})`;
  };

  const initHero = (hero) => {
    if (!hero || hero.dataset.wgeHeroBound === 'true') return;
    hero.dataset.wgeHeroBound = 'true';
    initRemote(hero);

    const stage = hero.querySelector('[data-wge-hero-stage]');
    const layers = Array.from(hero.querySelectorAll('[data-wge-hero-phase]'));
    const copy = hero.querySelector('[data-wge-hero-copy]');
    const phaseLabel = hero.querySelector('[data-wge-hero-phase-label]');
    const progressLabel = hero.querySelector('[data-wge-hero-progress]');
    let activeIndex = -1;
    let frame = 0;
    let enabled = false;

    const resetLayerState = () => {
      layers.forEach((layer, index) => {
        layer.classList.toggle('is-active', index === 0);
        layer.classList.remove('is-ready');
        layer.inert = false;
        layer.removeAttribute('aria-hidden');
        layer.style.removeProperty('opacity');
        const image = layer.querySelector('.wge-pc__image--hero');
        if (image) image.style.removeProperty('--wge-pc-camera-transform');
      });
      if (copy) {
        copy.inert = false;
        copy.removeAttribute('aria-hidden');
      }
      hero.style.removeProperty('--wge-pc-copy-opacity');
      hero.style.removeProperty('--wge-pc-copy-visibility');
    };

    const useTierC = (reason) => {
      hero.classList.remove('is-enhanced');
      hero.classList.add('is-tier-c');
      hero.dataset.wgeTierCReason = reason;
      hero.style.removeProperty('--wge-pc-hero-travel');
      resetLayerState();
    };

    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    const disable = (reason = 'rich-init-unavailable') => {
      enabled = false;
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      useTierC(reason);
    };

    const setActive = (index) => {
      if (index === activeIndex) return;
      activeIndex = index;
      const activeLayer = layers[index];
      if (phaseLabel) phaseLabel.textContent = activeLayer?.dataset.phaseLabel || '';
      if (progressLabel) {
        progressLabel.textContent = `${String(index + 1).padStart(2, '0')} / ${String(layers.length).padStart(2, '0')}`;
      }
    };

    function update() {
      frame = 0;
      if (!enabled || !stage) return;

      const rect = hero.getBoundingClientRect();
      const travel = Math.max(1, hero.offsetHeight - stage.offsetHeight);
      const progress = clamp(-rect.top / travel);
      const scaledProgress = progress * (layers.length - 1);
      const nextActive = Math.round(scaledProgress);

      layers.forEach((layer, index) => {
        const distance = index - scaledProgress;
        const opacity = clamp(1 - Math.abs(distance));
        const ready = Math.abs(distance) <= 1.05;
        const active = index === nextActive;
        layer.style.opacity = opacity.toFixed(3);
        layer.classList.toggle('is-ready', ready);
        layer.classList.toggle('is-active', active);
        layer.inert = !active;
        layer.setAttribute('aria-hidden', String(!active));

        const image = layer.querySelector('.wge-pc__image--hero');
        if (image) {
          image.style.setProperty('--wge-pc-camera-transform', cameraTransform(layer.dataset.wgeCamera, -distance));
        }
      });

      const copyOpacity = clamp(1 - progress * 5.5);
      hero.style.setProperty('--wge-pc-copy-opacity', copyOpacity.toFixed(3));
      hero.style.setProperty('--wge-pc-copy-visibility', copyOpacity < 0.03 ? 'hidden' : 'visible');
      if (copy) {
        const copyHidden = copyOpacity < 0.18;
        copy.inert = copyHidden;
        copy.setAttribute('aria-hidden', String(copyHidden));
      }
      setActive(nextActive);
    }

    const handleFailedImage = (image) => {
      const evidence = image.closest('[data-wge-tier-c-evidence]');
      const evidenceType = evidence?.dataset.wgeTierCEvidence || '';

      if (evidenceType === 'illumination-pair') {
        evidence.hidden = true;
        return;
      }

      if (evidenceType === 'entry' || evidenceType === 'return') {
        const alternateType = evidenceType === 'entry' ? 'return' : 'entry';
        const alternate = hero.querySelector(`[data-wge-tier-c-evidence="${alternateType}"] img`);
        const alternateSource = alternate ? (alternate.currentSrc || alternate.src) : '';
        const failedSource = image.currentSrc || image.src;

        if (alternateSource && alternateSource !== failedSource && image.dataset.wgeCriticalRetry !== 'true') {
          image.dataset.wgeCriticalRetry = 'true';
          image.srcset = alternate.srcset || '';
          image.sizes = alternate.sizes || image.sizes;
          image.src = alternateSource;
          return;
        }
      }

      if (evidence && evidenceType !== 'entry' && evidenceType !== 'return') evidence.hidden = true;
      else image.hidden = true;
    };

    let mediaFailed = false;
    hero.querySelectorAll('img').forEach((image) => {
      if (image.complete && image.naturalWidth === 0) {
        mediaFailed = true;
        handleFailedImage(image);
      }
      image.addEventListener('error', () => {
        handleFailedImage(image);
        disable('media-failure');
      });
    });
    if (mediaFailed) {
      useTierC('media-failure');
      return;
    }

    if (hero.dataset.enhanceReady !== 'true' || !stage || layers.length < 4) {
      useTierC('rich-init-unavailable');
      return;
    }
    if (reducedMotion.matches) {
      useTierC('reduced-motion');
      return;
    }

    enabled = true;
    hero.style.setProperty('--wge-pc-hero-travel', `${Math.max(3, layers.length - 1) * 66}svh`);
    hero.classList.remove('is-tier-c');
    delete hero.dataset.wgeTierCReason;
    hero.classList.add('is-enhanced');
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    reducedMotion.addEventListener('change', (event) => {
      if (event.matches) disable('reduced-motion');
    });
    update();
  };

  initFaq(root);
  initHero(root.querySelector('[data-wge-pc-hero]'));

  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-wge-return-top]');
    if (!link || reducedMotion.matches) return;

    const selector = link.getAttribute('href');
    const target = selector ? document.querySelector(selector) : null;
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
